/**
 * Workflow Types
 * TypeScript interfaces for trade workflow state machine configuration
 */

// Workflow states
export type WorkflowState =
  | 'Draft'
  | 'Booked'
  | 'Confirmed'
  | 'ContractSigned'
  | 'Final'
  | 'Cancelled'
  | 'Unwound';

// Product types that have workflows
export type WorkflowProductType = 'spot' | 'forward';

// Approval type (Front Office or Back Office)
export type ApprovalType = 'fo' | 'bo';

// Transition approval configuration for Spot trades
export interface SpotTransitionApprovals {
  draft_to_booked: number;
  booked_to_confirmed: number;
  confirmed_to_final: number;
  // Revert transitions
  booked_to_draft: number;
  confirmed_to_booked: number;
}

// Transition approval configuration for Forward trades
export interface ForwardTransitionApprovals {
  draft_to_booked: number;
  booked_to_confirmed: number;
  confirmed_to_contractSigned: number;
  contractSigned_to_final: number;
  // Revert transitions
  booked_to_draft: number;
  confirmed_to_booked: number;
  contractSigned_to_confirmed: number;
}

// Combined approval requirements
export interface ApprovalRequirements {
  spot: SpotTransitionApprovals;
  forward: ForwardTransitionApprovals;
}

// Full workflow configuration
export interface WorkflowConfig {
  approvalRequirements: ApprovalRequirements;
  demoMode: boolean;
  lastUpdatedAt?: string;
  lastUpdatedBy?: string;
}

// Update model (for PUT requests)
export interface WorkflowConfigUpdate {
  approvalRequirements: ApprovalRequirements;
  // Note: demoMode is NOT included - can only be set by platform owner directly in Firestore
}

// Default configuration values
export const DEFAULT_WORKFLOW_CONFIG: WorkflowConfig = {
  approvalRequirements: {
    spot: {
      draft_to_booked: 0,
      booked_to_confirmed: 1,
      confirmed_to_final: 0,
      booked_to_draft: 1,
      confirmed_to_booked: 1,
    },
    forward: {
      draft_to_booked: 0,
      booked_to_confirmed: 1,
      confirmed_to_contractSigned: 1,
      contractSigned_to_final: 0,
      booked_to_draft: 1,
      confirmed_to_booked: 1,
      contractSigned_to_confirmed: 1,
    },
  },
  demoMode: false,
};

// Transition metadata for UI rendering
export interface TransitionInfo {
  key: string;
  fromState: WorkflowState;
  toState: WorkflowState;
  label: string;
  approvalType: ApprovalType;
  isRevert: boolean;
}

// Spot trade transitions
export const SPOT_TRANSITIONS: TransitionInfo[] = [
  // Forward transitions
  { key: 'draft_to_booked', fromState: 'Draft', toState: 'Booked', label: 'Draft → Booked', approvalType: 'fo', isRevert: false },
  { key: 'booked_to_confirmed', fromState: 'Booked', toState: 'Confirmed', label: 'Booked → Confirmed', approvalType: 'bo', isRevert: false },
  { key: 'confirmed_to_final', fromState: 'Confirmed', toState: 'Final', label: 'Confirmed → Final', approvalType: 'bo', isRevert: false },
  // Revert transitions
  { key: 'booked_to_draft', fromState: 'Booked', toState: 'Draft', label: 'Booked → Draft', approvalType: 'bo', isRevert: true },
  { key: 'confirmed_to_booked', fromState: 'Confirmed', toState: 'Booked', label: 'Confirmed → Booked', approvalType: 'bo', isRevert: true },
];

// Forward trade transitions
export const FORWARD_TRANSITIONS: TransitionInfo[] = [
  // Forward transitions
  { key: 'draft_to_booked', fromState: 'Draft', toState: 'Booked', label: 'Draft → Booked', approvalType: 'fo', isRevert: false },
  { key: 'booked_to_confirmed', fromState: 'Booked', toState: 'Confirmed', label: 'Booked → Confirmed', approvalType: 'bo', isRevert: false },
  { key: 'confirmed_to_contractSigned', fromState: 'Confirmed', toState: 'ContractSigned', label: 'Confirmed → Contract Signed', approvalType: 'bo', isRevert: false },
  { key: 'contractSigned_to_final', fromState: 'ContractSigned', toState: 'Final', label: 'Contract Signed → Final', approvalType: 'bo', isRevert: false },
  // Revert transitions
  { key: 'booked_to_draft', fromState: 'Booked', toState: 'Draft', label: 'Booked → Draft', approvalType: 'bo', isRevert: true },
  { key: 'confirmed_to_booked', fromState: 'Confirmed', toState: 'Booked', label: 'Confirmed → Booked', approvalType: 'bo', isRevert: true },
  { key: 'contractSigned_to_confirmed', fromState: 'ContractSigned', toState: 'Confirmed', label: 'Contract Signed → Confirmed', approvalType: 'bo', isRevert: true },
];

// State metadata for UI rendering
export interface StateInfo {
  state: WorkflowState;
  label: string;
  isTerminal: boolean;
  icon: string; // Lucide icon name
}

// Spot workflow states
export const SPOT_STATES: StateInfo[] = [
  { state: 'Draft', label: 'Draft', isTerminal: false, icon: 'FileEdit' },
  { state: 'Booked', label: 'Booked', isTerminal: false, icon: 'BookOpen' },
  { state: 'Confirmed', label: 'Confirmed', isTerminal: false, icon: 'CheckCircle' },
  { state: 'Final', label: 'Final', isTerminal: true, icon: 'Flag' },
  { state: 'Cancelled', label: 'Cancelled', isTerminal: true, icon: 'XCircle' },
  { state: 'Unwound', label: 'Unwound', isTerminal: true, icon: 'Undo2' },
];

// Forward workflow states
export const FORWARD_STATES: StateInfo[] = [
  { state: 'Draft', label: 'Draft', isTerminal: false, icon: 'FileEdit' },
  { state: 'Booked', label: 'Booked', isTerminal: false, icon: 'BookOpen' },
  { state: 'Confirmed', label: 'Confirmed', isTerminal: false, icon: 'CheckCircle' },
  { state: 'ContractSigned', label: 'Contract Signed', isTerminal: false, icon: 'FileSignature' },
  { state: 'Final', label: 'Final', isTerminal: true, icon: 'Flag' },
  { state: 'Cancelled', label: 'Cancelled', isTerminal: true, icon: 'XCircle' },
  { state: 'Unwound', label: 'Unwound', isTerminal: true, icon: 'Undo2' },
];

// ============================================
// Workflow Exception Types (KAN-112)
// ============================================

export type AutoTransitionKey = 'booked_to_confirmed' | 'confirmed_to_contractSigned';

export interface WorkflowExceptionAction {
  autoTransitions: AutoTransitionKey[];
}

// ============================================
// Workflow Transition Block Types (KAN-199)
// ============================================

export type BlockableTransitionKey =
  | 'booked_to_confirmed'
  | 'confirmed_to_contractSigned'
  | 'confirmed_to_final'
  | 'contractSigned_to_final';

export interface TransitionBlockAction {
  transitions: BlockableTransitionKey[];
  requiredFields: string[];
}

export interface BlockedAutoTransition {
  transition: string;
  ruleName: string;
  ruleId: string;
  missingFields: string[];
  blockedAt: string;
}

// ============================================
// Workflow Operation Types (for API responses)
// ============================================

// Approval record in pending approval
export interface ApprovalRecord {
  userId: string;
  userName: string;
  timestamp: string;
  comment?: string;
}

// Pending approval info (stored in Firestore on trade document)
export interface PendingApproval {
  fromState: WorkflowState;
  targetState: WorkflowState;  // Note: backend uses 'targetState', not 'toState'
  requestedAt: string;
  requestedBy: string;
  requestedByName: string;
  requiredApprovers: number;
  currentApprovals: ApprovalRecord[];  // Note: backend uses 'currentApprovals'
  comment?: string;
}

// Response from GET /workflow/{clientId}/trades/{tradeId}/state
export interface TradeWorkflowState {
  currentState: WorkflowState;
  productType: WorkflowProductType;
  validTransitions: WorkflowState[];
  isTerminal: boolean;
  pendingApproval?: PendingApproval;
}

// Response from POST /workflow/{clientId}/trades/{tradeId}/transition
export interface TransitionResult {
  success: boolean;
  pendingApproval: boolean;
  requiredApprovers?: number;
  currentApprovers?: number;
  transition?: {
    fromState: WorkflowState;
    toState: WorkflowState;
    timestamp: string;
  };
}

// Response from POST /workflow/{clientId}/trades/{tradeId}/approve
export interface ApprovalResult {
  success: boolean;
  pendingApproval: boolean;
  currentApprovers?: number;
  requiredApprovers?: number;
  transition?: {
    fromState: WorkflowState;
    toState: WorkflowState;
    timestamp: string;
  };
}

// Workflow history event
export interface WorkflowHistoryEvent {
  type: 'transition' | 'approval_cancelled' | 'exception_auto_transition';
  fromState: WorkflowState;
  toState: WorkflowState;
  timestamp: string;
  triggeredBy: string;
  triggeredByName: string;
  auto?: boolean;
  comment?: string;
  approvalDetails?: {
    requiredApprovers: number;
    approvals: ApprovalRecord[];
  };
  reason?: string; // For cancelled approvals
  // Exception auto-transition fields (KAN-112)
  transitionKey?: string;  // e.g. "booked_to_confirmed"
  ruleId?: string;
  ruleName?: string;
  trigger?: string;        // "workflow_exception" for auto-transitions
  isRevert?: boolean;
}

// Pending approval item for list view
export interface PendingApprovalItem {
  tradeId: string;
  tradeNumber: string;
  productType: string;
  counterpartyName: string;
  fromState: WorkflowState;
  toState: WorkflowState;
  requestedAt: string;
  requestedBy: string;
  requestedByName: string;
  requiredApprovers: number;
  currentApprovers: number;
  approvals: ApprovalRecord[];
}
